import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, useTheme, alpha, useMediaQuery } from '@mui/material';
import { Button, IconButton, Loader, Typography } from '@sm-dashboard/ui-components';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { PersonalPage, getCurrencySymbol } from '@sm-dashboard/types';
import { getPersonalPagesUrl } from '@/constants';

const MOBILE_CAMPAIGNS_VISIBLE = 2;

const DonationsCampaignCard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileList = useMediaQuery(theme.breakpoints.down('md'));
  const [campaignsListExpanded, setCampaignsListExpanded] = useState(false);
  const navigate = useNavigate();
  const { loading: loadingCampaigns, fetchData: fetchCampaigns, data: campaignsData } = useFetch();

  useEffect(() => {
    const params = new URLSearchParams({
      rowsPerPage: '1000',
      pageType: 'campaign',
      forceShaliachId: 'true'
    });
    const url = `${getPersonalPagesUrl}?${params.toString()}`;
    fetchCampaigns({
      method: 'GET',
      url
    });
  }, [fetchCampaigns]);

  const campaigns = useMemo(() => {
    return (campaignsData?.rows || []) as PersonalPage[];
  }, [campaignsData]);

  const totals = useMemo(() => {
    let sales = 0;
    let donors = 0;
    const currencyCodes = new Set<string>();
    campaigns.forEach(c => {
      const analytics = (c as unknown as { analytics?: { totalAmount?: number; totalSold?: number } }).analytics || {};
      sales += Number(analytics.totalAmount || 0);
      donors += Number(analytics.totalSold || 0);
      currencyCodes.add(String(c.defaultCurrency || 'ILS').toUpperCase());
    });
    const sharedCurrency = currencyCodes.size === 1 ? [...currencyCodes][0] : null;
    return { sales, donors, currencySymbol: sharedCurrency ? getCurrencySymbol(sharedCurrency) : null };
  }, [campaigns]);

  const handleView = (c: PersonalPage & { _id?: string | number }) => {
    if (!c._id) return;
    navigate(`/desk/donations?personalPageId=${encodeURIComponent(String(c._id))}`);
  };

  const visibleCampaigns = useMemo(() => {
    if (!isMobileList || campaignsListExpanded) {
      return campaigns;
    }
    return campaigns.slice(0, MOBILE_CAMPAIGNS_VISIBLE);
  }, [campaigns, isMobileList, campaignsListExpanded]);

  const showCampaignsExpandControl = isMobileList && campaigns.length > MOBILE_CAMPAIGNS_VISIBLE;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          bgcolor: 'success.main',
          color: 'common.white',
          p: 1,
          pl: 2,
          borderRadius: '30px 30px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexDirection: 'row',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <VolunteerActivismIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Typography variant="h6">הקמפיינים שלי</Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          borderRadius: '0 0 20px 20px',
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.grey[300], 0.5),
          display: 'flex',
          flexDirection: 'column',
          minHeight: {
            xs: isMobileList && !campaignsListExpanded ? 'auto' : 280,
            sm: 360
          },
          height: { xs: 'auto', sm: 360 },
          maxHeight: { xs: isMobileList && campaignsListExpanded ? 400 : 'none', sm: 'none' },
          p: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            gap: { xs: 2, sm: 4 },
            bgcolor: theme.palette.grey[200],
            overflow: 'hidden'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ mb: 0.5 }}>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                תרומות
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                {totals.sales.toLocaleString('he-IL')}
              </Typography>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                {totals.currencySymbol ?? ''}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box sx={{ mb: 0.5 }}>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                תורמים
              </Typography>
            </Box>
            <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
              {totals.donors?.toLocaleString('he-IL') ?? '0'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: isMobileList && !campaignsListExpanded ? 'none' : 1,
            overflowY: isMobileList && !campaignsListExpanded ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: 1,
            minHeight: 0
          }}
        >
          {loadingCampaigns ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Loader size={32} thickness={5} />
            </Box>
          ) : campaigns.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ color: 'text.secondary' }}>
                <Typography variant="body">אין קמפיינים להצגה</Typography>
              </Box>
            </Box>
          ) : (
            visibleCampaigns.map(c => {
              const analytics =
                (c as unknown as { analytics?: { totalSold?: number; totalAmount?: number } }).analytics || {};
              return (
                <Box
                  key={c._id || c.id}
                  onClick={() => handleView(c)}
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    borderRadius: 1,
                    bgcolor: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexDirection: 'row-reverse',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.grey[50], 0.8),
                      transform: 'translateX(-2px)'
                    }
                  }}
                >
                  <Box sx={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'auto' }}>
                    <IconButton
                      color="primary"
                      shape="circle"
                      size="small"
                      aria-label="צפייה בקמפיין"
                      onClick={e => {
                        e.stopPropagation();
                        handleView(c);
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 15, transform: 'rotate(180deg)' }} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      gap: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <Box sx={{ width: { xs: '100%', sm: '30%' }, flexShrink: 0, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Typography variant="subtitle2" color="text.primary">
                          {(c as any).name || c.title || 'שם הקמפיין'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        overflow: 'hidden',
                        minWidth: 0
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: 'success.main',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          flexShrink: 0,
                          lineHeight: 1
                        }}
                      >
                        ₪
                      </Box>
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}
                      >
                        <Typography variant="body" color="text.secondary">
                          {Number(analytics.totalAmount || 0).toLocaleString('he-IL')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
        {showCampaignsExpandControl && (
          <Box sx={{ pt: 1, pb: 0.5, px: 0.5, flexShrink: 0 }}>
            <Button
              fullWidth
              color="warning"
              variant="contained"
              onClick={() => setCampaignsListExpanded(prev => !prev)}
            >
              {campaignsListExpanded ? 'הצג פחות' : `הצג את כל הקמפיינים (${campaigns.length})`}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DonationsCampaignCard;
